import {
  useAllIncidents,
  useIncident,
  useIsInitialResponse,
} from '../hooks/incidentHooks';
import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { registerIncidentLifecycleTourApi } from 'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTourBridge';
import {
  INCIDENT_TOUR_CONTINUE_KEY,
  INCIDENT_TOUR_PARAM,
  incidentTourArchiveHref,
  incidentTourPlanningHref,
  wantsIncidentTour,
} from 'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTourSteps';
import { resolveIncidentTourPlanningHref } from 'components/IncidentConfig/incidentLifecycleTour/incidentTourTargets';
import InitialResponseStepper from './InitialResponseStepper';
import InitialResponseOverview from './InitialResponseOverview';
import InitialResponseObjectivesActions from './InitialResponseObjectivesActions';
import { InitialResponseTabsType } from './InitialResponseTabs';
import { InitialResponseResources } from './InitialResponseResources';
import InitialResponseBriefing from './InitialResponseBriefing';
import SafetyAnalysis from './SafetyAnalysis/SafetyAnalysis';
import classNames from 'classnames';
import ReportsView from 'components/Reports/ReportsView';
import { useICSContext } from '../planningView/ICSContext';
import IAPRoster from '../IAPRoster';
import { UscgMapLayout } from 'components/MapPage/UscgMapLayout';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  CorporateViewTabGroup,
  CorporateViewTabs,
} from 'components/MapPage/CorporateViewTabGroup';
import InitialResponseFormsView from './InitialResponseFormsView';
import InitialResponseViewToggle from './InitialResponseViewToggle';
import { useInitialResponseView } from './useInitialResponseView';
import { FOC_INITIAL_RESPONSE_TOUR_PARAM } from 'components/MapPage/focInitialResponseTour/focInitialResponseTourSteps';
import {
  getFocInitialResponseTourApi,
  registerFocInitialResponseTourApi,
} from 'components/MapPage/focInitialResponseTour/focInitialResponseTourBridge';
import { FOC_TRANSITION_TOUR_PARAM } from 'components/MapPage/focTransitionTour/focTransitionTourSteps';
import { registerFocTransitionTourApi } from 'components/MapPage/focTransitionTour/focTransitionTourBridge';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { ICS_201_WIDGET_NAME } from '../planningView/cardOptions';

// Maps context tab values to InitialResponseTabsType
const TAB_CONTEXT_MAP: Record<string, InitialResponseTabsType> = {
  'objectives-actions': 'Objectives & Actions',
  overview: 'Overview',
  'incident-roster': 'Incident Roster',
  resources: 'Resources',
  'safety-analysis': 'Safety Analysis',
  'incident-briefing': 'Incident Briefing',
  reports: 'Reports',
};

/** Map sidebar openTab values that have an Initial Response stepper equivalent. */
const OPEN_TAB_TO_IR: Partial<
  Record<CorporateViewTabs, InitialResponseTabsType>
> = {
  [CorporateViewTabs.Reports]: 'Reports',
  [CorporateViewTabs.Resources]: 'Resources',
};

const IR_TO_OPEN_TAB: Partial<
  Record<InitialResponseTabsType, CorporateViewTabs>
> = {
  Reports: CorporateViewTabs.Reports,
  Resources: CorporateViewTabs.Resources,
};

const isDualSurfaceOpenTab = (
  openTab: string | null
): openTab is CorporateViewTabs =>
  openTab === CorporateViewTabs.Reports ||
  openTab === CorporateViewTabs.Resources ||
  openTab === CorporateViewTabs.Actions;

const InitialResponseReports = () => {
  return <ReportsView />;
};

const PhaseMap: Record<InitialResponseTabsType, React.ReactNode> = {
  Overview: <InitialResponseOverview />,
  'Objectives & Actions': <InitialResponseObjectivesActions />,
  'Incident Roster': <IAPRoster />,
  Resources: <InitialResponseResources />,
  'Safety Analysis': <SafetyAnalysis />,
  'Incident Briefing': <InitialResponseBriefing />,
  Reports: <InitialResponseReports />,
};

const InitialResponsePage = () => {
  const mainContentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { incidentId } = useParams<{ incidentId: string }>();
  const { isMapVisible, setIsMapVisible } = useUscgMapContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { view, setView } = useInitialResponseView();
  // Enforced here rather than only at the dashboard, because `/iap/:incidentId/initial-response` mounts this page
  // directly and is where a freshly created incident lands. An escalated incident reaching it belongs to Planning P.
  const isInitialResponse = useIsInitialResponse();
  const prevMapVisibleRef = useRef(isMapVisible);
  const openTab = searchParams.get('openTab');
  const showCorporateView =
    isMapVisible || openTab === CorporateViewTabs.Actions;

  const incident = useIncident();

  // Read initial tab from ICSContext (set by AI citation navigation)
  const { initialResponseTab, setInitialResponseTab } = useICSContext();
  const getInitialTab = (): InitialResponseTabsType => {
    if (initialResponseTab && TAB_CONTEXT_MAP[initialResponseTab]) {
      // Clear context after reading to prevent stale navigation
      setInitialResponseTab('');
      return TAB_CONTEXT_MAP[initialResponseTab];
    }
    return 'Overview';
  };
  const [currentTab, setCurrentTab] =
    useState<InitialResponseTabsType>(getInitialTab);

  /**
   * Keep openTab aligned with IR dual-surface tabs so map open paints the right
   * Corporate View tab.
   */
  const syncOpenTabForIrTab = useCallback(
    (irTab: InitialResponseTabsType) => {
      const mappedOpenTab = IR_TO_OPEN_TAB[irTab];
      setSearchParams(
        (params) => {
          if (mappedOpenTab) {
            params.set('openTab', mappedOpenTab);
            return params;
          }
          const existing = params.get('openTab');
          if (isDualSurfaceOpenTab(existing)) {
            params.set('openTab', CorporateViewTabs.Notifications);
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleIrTabChange = useCallback(
    (nextTab: InitialResponseTabsType) => {
      setCurrentTab(nextTab);
      if (!isMapVisible) {
        syncOpenTabForIrTab(nextTab);
      }
    },
    [isMapVisible, syncOpenTabForIrTab]
  );

  // Sync map sidebar openTab <-> Initial Response stepper on map open/close.
  useEffect(() => {
    const wasMapVisible = prevMapVisibleRef.current;
    if (wasMapVisible === isMapVisible) return;
    prevMapVisibleRef.current = isMapVisible;

    if (!isMapVisible) {
      // Map closed: apply Reports/Resources from openTab to the IR stepper.
      const openTab = searchParams.get('openTab') as CorporateViewTabs | null;
      if (openTab && OPEN_TAB_TO_IR[openTab]) {
        setCurrentTab(OPEN_TAB_TO_IR[openTab]!);
      }
      return;
    }

    // Map opened: align openTab with IR dual-surface tabs (write or clear stale).
    syncOpenTabForIrTab(currentTab);
  }, [isMapVisible, currentTab, searchParams, syncOpenTabForIrTab]);

  // Incident Lifecycle tour: this is where a real create lands, so the roster,
  // tasks, and reports steps run against the stepper here.
  const handleIrTabChangeRef = useRef(handleIrTabChange);
  handleIrTabChangeRef.current = handleIrTabChange;
  const incidentIdRef = useRef(incident.data?.id);
  incidentIdRef.current = incident.data?.id;
  // Planning P only exists on an escalated incident, which this one is not
  // (it would have redirected), so the tour borrows another one if it can.
  const allIncidents = useAllIncidents();
  const planningHref = resolveIncidentTourPlanningHref(
    incident.data?.id ? String(incident.data.id) : undefined,
    allIncidents.data ?? []
  );
  const planningHrefRef = useRef(planningHref);
  planningHrefRef.current = planningHref;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const wantsTour = wantsIncidentTour(
    searchParams.get(INCIDENT_TOUR_PARAM),
    'workspace'
  );
  const tourStartedRef = useRef(false);
  const uscgFoc = useUscgFocRequirementsFlag();
  const wantsFocIrTour =
    uscgFoc && searchParams.get(FOC_INITIAL_RESPONSE_TOUR_PARAM) === '1';
  const focIrTourStartedRef = useRef(false);
  const wantsFocTransitionTour =
    uscgFoc && searchParams.get(FOC_TRANSITION_TOUR_PARAM) === '1';
  const focTransitionTourStartedRef = useRef(false);

  useEffect(
    () =>
      registerIncidentLifecycleTourApi({
        setWorkspacePhase: (phase) => handleIrTabChangeRef.current(phase),
        setWorkspaceView: (nextView) => setView(nextView),
        prepareWorkspaceSurface: () => {
          setIsMapVisible(false);
          setView('legacy');
        },
        navigateTo: (href) => navigateRef.current(href),
        resolvePlanningHref: () => planningHrefRef.current,
        resolveArchiveHref: () => {
          const id = incidentIdRef.current;
          return id ? incidentTourArchiveHref(String(id)) : null;
        },
      }),
    [setIsMapVisible, setView]
  );

  // FOC 8.x bridge: page always mounts (FormsView unmounts when the map opens).
  useEffect(
    () =>
      registerFocInitialResponseTourApi({
        getLocation: () => ({
          pathname: window.location.pathname,
          search: window.location.search,
        }),
        openReportsSurface: () => {
          setIsMapVisible(true);
          setSearchParams(
            (params) => {
              params.set('openTab', CorporateViewTabs.Reports);
              return params;
            },
            { replace: true }
          );
        },
        ensureNewIcs201: () => {
          setIsMapVisible(false);
          setView('new');
          setSearchParams(
            (params) => {
              params.delete('openTab');
              return params;
            },
            { replace: true }
          );
          // FormsView may still be mounted; select the editable 201 card.
          window.setTimeout(() => {
            getFocInitialResponseTourApi().selectFormsCard?.(
              ICS_201_WIDGET_NAME
            );
          }, 0);
        },
        scrollToTour: (tour) => {
          window.setTimeout(() => {
            const el = document.querySelector(`[data-tour="${tour}"]`);
            if (el instanceof HTMLElement) {
              el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 50);
        },
      }),
    [setIsMapVisible, setView, setSearchParams]
  );

  // FOC 9.1 IR surface: force New + shared ensure/scroll helpers.
  useEffect(
    () =>
      registerFocTransitionTourApi({
        getLocation: () => ({
          pathname: window.location.pathname,
          search: window.location.search,
        }),
        ensureNewIcs201: () => {
          setIsMapVisible(false);
          setView('new');
          window.setTimeout(() => {
            getFocInitialResponseTourApi().selectFormsCard?.(
              ICS_201_WIDGET_NAME
            );
          }, 0);
        },
        navigateTo: (href) => navigateRef.current(href),
        scrollToTour: (tour) => {
          window.setTimeout(() => {
            const el = document.querySelector(`[data-tour="${tour}"]`);
            if (el instanceof HTMLElement) {
              el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 50);
        },
      }),
    [setIsMapVisible, setView]
  );

  useEffect(() => {
    if (!wantsTour || !incident.data) return;
    if (isMapVisible) setIsMapVisible(false);
    if (view !== 'legacy') setView('legacy');
  }, [wantsTour, incident.data, isMapVisible, view, setIsMapVisible, setView]);

  // FOC 8.x / 9.1: force New ICS 201 while the IR tour param is present.
  useEffect(() => {
    if ((!wantsFocIrTour && !wantsFocTransitionTour) || !incident.data) return;
    if (isMapVisible) setIsMapVisible(false);
    if (view !== 'new') setView('new');
  }, [
    wantsFocIrTour,
    wantsFocTransitionTour,
    incident.data,
    isMapVisible,
    view,
    setIsMapVisible,
    setView,
  ]);

  useEffect(() => {
    if (!wantsFocIrTour || focIrTourStartedRef.current || !incident.data) {
      return;
    }
    if (isMapVisible || view !== 'new') return;

    const timer = window.setTimeout(() => {
      if (focIrTourStartedRef.current) return;
      focIrTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_INITIAL_RESPONSE_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import(
        'components/MapPage/focInitialResponseTour/focInitialResponseTour'
      )
        .then(async ({ startFocInitialResponseTour, waitForSelector }) => {
          await waitForSelector('[data-tour="incident-ir-forms"]');
          startFocInitialResponseTour();
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [
    wantsFocIrTour,
    incident.data,
    isMapVisible,
    view,
    setSearchParams,
  ]);

  // FOC 9.1: start Transition tour on New ICS 201 (escalate happens mid-tour).
  useEffect(() => {
    if (
      !wantsFocTransitionTour ||
      focTransitionTourStartedRef.current ||
      !incident.data
    ) {
      return;
    }
    if (isMapVisible || view !== 'new') return;

    const timer = window.setTimeout(() => {
      if (focTransitionTourStartedRef.current) return;
      focTransitionTourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(FOC_TRANSITION_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import('components/MapPage/focTransitionTour/focTransitionTour')
        .then(async ({ startFocTransitionTour, waitForSelector }) => {
          await waitForSelector('[data-tour="incident-escalate"]');
          startFocTransitionTour();
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [
    wantsFocTransitionTour,
    incident.data,
    isMapVisible,
    view,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!wantsTour || tourStartedRef.current || !incident.data) return;
    if (isMapVisible || view !== 'legacy') return;

    const timer = window.setTimeout(() => {
      if (tourStartedRef.current) return;
      tourStartedRef.current = true;
      setSearchParams(
        (params) => {
          params.delete(INCIDENT_TOUR_PARAM);
          return params;
        },
        { replace: true }
      );
      void import(
        'components/IncidentConfig/incidentLifecycleTour/incidentLifecycleTour'
      )
        .then(async ({ startIncidentLifecycleTour, waitForSelector }) => {
          await waitForSelector('[data-tour="incident-ir-stepper"]');
          startIncidentLifecycleTour('workspace');
        })
        .catch(() => {});
    }, 400);

    return () => window.clearTimeout(timer);
  }, [wantsTour, incident.data, isMapVisible, view, setSearchParams]);

  const mainContentClasses = classNames('maincontent iap-response__main', {
    'maincontent-no-padding': showCorporateView,
    'h-100': showCorporateView,
    // The forms view scrolls inside the main area rather than being clipped by it, which needs the column it sits in.
    'iap-response__main--forms': !showCorporateView && view === 'new',
  });

  if (incident.data === undefined) {
    return null;
  }

  if (!isInitialResponse) {
    const continuePlanning =
      sessionStorage.getItem(INCIDENT_TOUR_CONTINUE_KEY) === 'planning';
    return (
      <Navigate
        to={
          continuePlanning && incidentId
            ? incidentTourPlanningHref(incidentId)
            : `/iap/${incidentId}/dashboard`
        }
        replace
      />
    );
  }

  return (
    <div className="iap-response">
      {showCorporateView ? (
        <div className={mainContentClasses} ref={mainContentRef}>
          <UscgMapLayout>
            <CorporateViewTabGroup
              ics={true}
              currentMode={''}
              updateMode={() => {}}
              currentPhase={0}
            />
          </UscgMapLayout>
        </div>
      ) : (
        <div className={mainContentClasses} ref={mainContentRef}>
          {/* New shares the forms dropdown row with the toggle. Legacy has no such row, so it keeps a strip of its own. */}
          {view === 'legacy' ? (
            <div className="d-flex justify-content-end mt-3 flex-shrink-0">
              <InitialResponseViewToggle view={view} onChange={setView} />
            </div>
          ) : null}

          {view === 'new' ? (
            <div className="iap-response__forms-scroll">
              <div data-tour="incident-ir-content">
                <InitialResponseFormsView view={view} onViewChange={setView} />
              </div>
            </div>
          ) : (
            <>
              <InitialResponseStepper
                currentPhase={currentTab}
                updatePhase={handleIrTabChange}
              />

              <div className="planning-container no-header">
                <div data-tour="incident-ir-content">
                  {PhaseMap[currentTab]}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
export default InitialResponsePage;
